import React from 'react';
import { Table, Form } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from "../helpers/Fetchwrapper";
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';

interface ItemData {
    id_items: Number;
    item: {
        item_name: string;
        unit?: {
            unit_name: string;
        } | null;
    };
    item_category: {
        category_name: string;
    } | null;
    consumed_quantity: Number;
    consumed_amount: Number;
}

interface StockUsageData {
    id_products: Number;
    product: {
        product_name: string;
        packing: string;
        size: string;
        id_units: Number;
        sale_per_unit?: Number;
    };
    unit: {
        unit_name: string;
    } | null;
    sold_quantity: Number;
    sold_amount: Number;
    items: ItemData[];
}

interface StockUsagePredictionProps {

}

const StockUsagePrediction: React.FC<StockUsagePredictionProps> = (props) => {

    const [loading, setLoading] = useState(false);
    const [stockUsageData, setStockUsageData] = useState<StockUsageData[]>([]);
    const [modifiedSoldQuantities, setModifiedSoldQuantities] = useState<{ [key: number]: number }>({});
    const [formData, setFormData] = useState({
        idProjects: '',
        fromDate: '',
        toDate: '',
    });

    const [projects, setProjects] = useState<any[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });

    useEffect(() => {
        getProjects();
    }, [])

    useEffect(() => {
        if (projects.length > 0) {
            setProject(projects[0]);
            setFormData((prevFormData) => ({
                ...prevFormData,
                idProjects: projects[0].value,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);
    
    const getProjects = () => {
        fetch(
            process.env.REACT_APP_API_URL + 'lead/projects',
            getRequestOptions()
        )
            .then((resp) => {
                return resp.json()
            })
            .then((resp) => {
                let projectObj = resp.data.map((item: any) => {
                    item.label = item.name;
                    item.value = item.id;
                    return item
                })
                setProjects(projectObj);
            })
            .catch((error) => {
                console.log(error, 'catch the hoop')
            })
    }

    const handleProjectChange = (project: any) => {
        setProject(project);
        if (project) {
            setFormData({ ...formData, idProjects: project.value });
        } else {
            setFormData({ ...formData, idProjects: '' });
        }
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleViewReport = async () => {
        setLoading(true);

        try {
            const response = await fetch(process.env.REACT_APP_API_URL + 'reports/stock-usage-prediction', postRequestOptions(formData));
            const data = await response.json();

            if (data.success) {
                setStockUsageData(data.data);
                setModifiedSoldQuantities({}); // Reset modified quantities
            } else {
                setStockUsageData([]);
                setModifiedSoldQuantities({});
                data.errors.forEach((error: string) => toast.error(error, { position: 'bottom-right', autoClose: 4000 }));
            }
        } catch (error) {
            console.error('Error submitting data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSoldQuantityChange = (productId: number, newSoldQuantity: number, originalSoldQuantity: number) => {
        setModifiedSoldQuantities((prev) => ({
            ...prev,
            [productId]: newSoldQuantity
        }));
    };

    const getSoldQuantity = (productData: StockUsageData) => {
        const productId = Number(productData.id_products);
        return modifiedSoldQuantities[productId] !== undefined 
            ? modifiedSoldQuantities[productId] 
            : Number(productData.sold_quantity);
    };

    const getMultiplier = (productData: StockUsageData) => {
        const originalSoldQty = Number(productData.sold_quantity);
        const currentSoldQty = getSoldQuantity(productData);
        if (originalSoldQty === 0) return 1;
        return currentSoldQty / originalSoldQty;
    };

    const getCalculatedConsumedQuantity = (item: ItemData, productData: StockUsageData) => {
        const multiplier = getMultiplier(productData);
        return Number(item.consumed_quantity) * multiplier;
    };

    const getCalculatedConsumedAmount = (item: ItemData, productData: StockUsageData) => {
        const multiplier = getMultiplier(productData);
        return Number(item.consumed_amount) * multiplier;
    };

    const getCalculatedSoldAmount = (productData: StockUsageData) => {
        const soldQuantity = getSoldQuantity(productData);
        const salesPrice = Number(productData.product?.sale_per_unit ?? 0);
        return soldQuantity * salesPrice;
    };

    const queryParams = new URLSearchParams(formData);

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Title></Card.Title>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control type="date" placeholder="From Date" name='fromDate' value={formData.fromDate} onChange={handleChange} />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control type="date" placeholder="To Date" name='toDate' value={formData.toDate} onChange={handleChange} />
                                </Form.Group>
                            </div>
                            <div className='col-md-1'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary w-100 p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                </div>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <a className="btn btn-sm btn-primary w-100 p-3 mb-2" href={`${process.env.REACT_APP_BACKEND}stock_usage_prediction_download?` + queryParams.toString()}><i className='fas fa-download'></i>Excel Download</a>
                                </div>
                            </div>
                        </div>

                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    <Table responsive bordered hover>
                        <thead>
                            <tr>
                                <th className='text-center'>Product Name</th>
                                <th className='text-center'>Packing</th>
                                <th className='text-center'>Sold Quantity</th>
                                <th className='text-center'>Sales Price</th>
                                <th className='text-center'>Sold Amount</th>
                                <th className='text-center'>Item Name</th>
                                <th className='text-center'>Item Category</th>
                                <th className='text-center'>Item Unit</th>
                                <th className='text-center'>Consumed Quantity</th>
                                <th className='text-center'>Consumed Amount</th>
                            </tr>
                        </thead>
                        
                        <tbody>
                           {stockUsageData.map((productData: any, productIndex: number) => (
                                productData.items.map((item: any, itemIndex: number) => (
                                <tr key={`${productIndex}-${itemIndex}`}>
                                    {itemIndex === 0 && (
                                    <>
                                        <td rowSpan={productData.items.length}>
                                            {productData.product?.product_name ?? ''}
                                        </td>
                                        <td rowSpan={productData.items.length}>
                                            {productData.product?.packing ?? ''}
                                        </td>
                                        <td rowSpan={productData.items.length}>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                // step="0.01"
                                                value={getSoldQuantity(productData)}
                                                onChange={(e) => {
                                                    const newValue = parseFloat(e.target.value) || 0;
                                                    handleSoldQuantityChange(
                                                        Number(productData.id_products),
                                                        newValue,
                                                        Number(productData.sold_quantity)
                                                    );
                                                }}
                                                style={{ width: '100px' }}
                                            />
                                        </td>
                                        <td rowSpan={productData.items.length}>
                                            {numberFormat(productData.product?.sale_per_unit ?? 0)}
                                        </td>
                                        <td rowSpan={productData.items.length}>
                                            {(() => {
                                                const productId = Number(productData.id_products);
                                                const isQuantityModified = modifiedSoldQuantities[productId] !== undefined;
                                                if (isQuantityModified) {
                                                    return numberFormat(getCalculatedSoldAmount(productData));
                                                }
                                                return numberFormat(productData.sold_amount ?? 0);
                                            })()}
                                        </td>
                                    </>
                                    )}
                                    <td>{item.item?.item_name ?? ''}</td>
                                    <td>{item.item_category?.category_name ?? ''}</td>
                                    <td>{item.item?.unit?.unit_name ?? ''}</td>
                                    <td>{numberFormat(getCalculatedConsumedQuantity(item, productData))}</td>
                                    <td>{numberFormat(getCalculatedConsumedAmount(item, productData))}</td>
                                </tr>
                                ))
                            ))}
                            
                        </tbody>

                    </Table>
                </div>
            </div>
        </>
    );
};

export default StockUsagePrediction;

